// src/services/basketballApi.js

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost/DiceAce12/backend';

// Format odds
export const formatOddsValue = (odds, format = 'decimal') => {
    if (!odds || odds === null) return '-';
    
    const numOdds = parseFloat(odds);
    if (isNaN(numOdds)) return '-';
    
    if (format === 'decimal') {
        return numOdds.toFixed(2);
    } else if (format === 'fractional') {
        const fractional = (numOdds - 1).toFixed(2);
        return `${fractional}/1`;
    } else if (format === 'american') {
        if (numOdds >= 2.0) {
            return `+${Math.round((numOdds - 1) * 100)}`;
        } else {
            return `-${Math.round(100 / (numOdds - 1))}`;
        }
    }
    return numOdds.toFixed(2);
};

// Convert decimal to American for display
export const decimalToAmerican = (decimal) => {
    if (!decimal || decimal === null) return null;
    if (decimal >= 2.0) {
        return `+${Math.round((decimal - 1) * 100)}`;
    } else {
        return `-${Math.round(100 / (decimal - 1))}`;
    }
};

// Basketball status mapping (different from football)
export const getBasketballStatusText = (status) => {
    const statusMap = {
        'NS': 'Not Started',
        '1Q': '1st Quarter',
        '2Q': '2nd Quarter',
        'HT': 'Half Time',
        '3Q': '3rd Quarter',
        '4Q': '4th Quarter',
        'OT': 'Overtime',
        'FT': 'Finished',
        'AOT': 'After Overtime',
        'CANC': 'Cancelled',
        'PST': 'Postponed'
    };
    return statusMap[status] || status;
};

// Get status badge color
export const getBasketballStatusColor = (status) => {
    const colorMap = {
        'NS': 'info',
        '1Q': 'danger',
        '2Q': 'danger',
        'HT': 'warning',
        '3Q': 'danger',
        '4Q': 'danger',
        'OT': 'danger',
        'FT': 'success',
        'AOT': 'success',
        'CANC': 'secondary',
        'PST': 'secondary'
    };
    return colorMap[status] || 'secondary';
};

// API functions
export const BasketballAPI = {
    // Get games grouped by league for a specific date
    getGames: async (date) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/games/${date}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch basketball games:', error);
            return { data: [], error: error.message };
        }
    },
    
    getUpcoming: async (limit = 100) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/upcoming?limit=${limit}`, {
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch upcoming basketball games:', error);
            return { data: [], error: error.message };
        }
    },
    
    // Get live games
    getLiveGames: async () => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/live`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch live basketball games:', error);
            return { data: [] };
        }
    },
    
    // Get odds for a specific game
    getOdds: async (gameId) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/odds/${gameId}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch basketball odds:', error);
            return { data: [] };
        }
    },
    
    // Get complete match details
    getMatchDetails: async (gameId) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/match/${gameId}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                // If 404, return a specific error
                if (response.status === 404) {
                    return { success: false, error: 'Match not found' };
                }
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch basketball match details:', error);
            return { success: false, error: error.message };
        }
    },
    
    // Get available dates
    getAvailableDates: async () => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/dates`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch available dates:', error);
            return { data: [] };
        }
    },
    
    // Get leagues for a date
    getLeagues: async (date) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/leagues?date=${date}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch basketball leagues:', error);
            return { data: [] };
        }
    },
    
    // Get teams by search
    getTeams: async (search) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/teams?search=${encodeURIComponent(search)}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch basketball teams:', error);
            return { data: [] };
        }
    },
    
    // Get team statistics
    getTeamStats: async (teamId) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/team-stats?team_id=${teamId}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch basketball team stats:', error);
            return { data: null };
        }
    },
    
    // Get upcoming games
    getUpcomingGames: async (limit = 50) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/upcoming?limit=${limit}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch upcoming basketball games:', error);
            return { data: [] };
        }
    },
    
    // Get recent results
    getResults: async (date) => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/results?date=${date}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch basketball results:', error);
            return { data: [] };
        }
    },
    
    // Get live score updates (for AJAX polling)
    getLiveUpdates: async (lastUpdate = null) => {
        try {
            let url = `${API_BASE}/api/basketball/live-updates`;
            if (lastUpdate) {
                url += `?last_update=${encodeURIComponent(lastUpdate)}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch basketball live updates:', error);
            return { data: [] };
        }
    },
    
    // Get active leagues
    getActiveLeagues: async () => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/active-leagues`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch active basketball leagues:', error);
            return { data: [] };
        }
    },
    
    // Refresh data from API
    refreshData: async () => {
        try {
            const response = await fetch(`${API_BASE}/api/basketball/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to refresh basketball data:', error);
            return { success: false, error: error.message };
        }
    }
};

// Helper: Format game time
export const formatGameTime = (game) => {
    if (!game) return '';
    
    const status = game.status || 'NS';
    const elapsed = game.elapsed || '';
    
    if (['1Q', '2Q', '3Q', '4Q', 'OT'].includes(status)) {
        return `${status} ${elapsed}`;
    }
    
    if (status === 'HT') return 'Half Time';
    if (status === 'FT') return 'Finished';
    if (status === 'NS') {
        const date = new Date(game.fixture_date);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    return status;
};

// Helper: Get period scores display
export const getPeriodScores = (periodScore) => {
    if (!periodScore) return [];
    
    try {
        const periods = typeof periodScore === 'string' ? JSON.parse(periodScore) : periodScore;
        if (!periods || typeof periods !== 'object') return [];
        
        const periodKeys = Object.keys(periods).filter(key => key.startsWith('Q') || key.startsWith('OT'));
        return periodKeys.map(key => ({
            period: key,
            home: periods[key]?.home ?? 0,
            away: periods[key]?.away ?? 0
        }));
    } catch (e) {
        return [];
    }
};

// Helper: Format odds for display
export const formatBasketballOdds = (odds) => {
    if (!odds) return {};
    
    const result = {};
    
    // Check for match_winner (moneyline)
    if (odds.match_winner) {
        result.moneyline = {
            home: odds.match_winner.home_odds ? parseFloat(odds.match_winner.home_odds) : null,
            away: odds.match_winner.away_odds ? parseFloat(odds.match_winner.away_odds) : null
        };
    }
    
    // Check for point_spread
    if (odds.point_spread) {
        result.point_spread = {
            home: odds.point_spread.home_spread_odds ? parseFloat(odds.point_spread.home_spread_odds) : null,
            away: odds.point_spread.away_spread_odds ? parseFloat(odds.point_spread.away_spread_odds) : null,
            value: odds.point_spread.spread_value ? parseFloat(odds.point_spread.spread_value) : null
        };
    }
    
    // Check for total_points
    if (odds.total_points) {
        result.total_points = {
            over: odds.total_points.over_odds ? parseFloat(odds.total_points.over_odds) : null,
            under: odds.total_points.under_odds ? parseFloat(odds.total_points.under_odds) : null,
            value: odds.total_points.total_points_value ? parseFloat(odds.total_points.total_points_value) : null
        };
    }
    
    // Check for quarter winner odds
    if (odds.q1_winner_odds || odds.q2_winner_odds || odds.q3_winner_odds || odds.q4_winner_odds) {
        result.quarter_winner = {
            q1: odds.q1_winner_odds ? JSON.parse(odds.q1_winner_odds) : null,
            q2: odds.q2_winner_odds ? JSON.parse(odds.q2_winner_odds) : null,
            q3: odds.q3_winner_odds ? JSON.parse(odds.q3_winner_odds) : null,
            q4: odds.q4_winner_odds ? JSON.parse(odds.q4_winner_odds) : null
        };
    }
    
    // Check for half winner odds
    if (odds.half_winner_odds) {
        result.half_winner = JSON.parse(odds.half_winner_odds);
    }
    
    // Check for team total odds
    if (odds.team_total_odds) {
        result.team_total = JSON.parse(odds.team_total_odds);
    }
    
    // Check for player props odds
    if (odds.player_props_odds) {
        result.player_props = JSON.parse(odds.player_props_odds);
    }
    
    // If no specific basketball odds were found but we have match_winner, use it as fallback
    if (!result.moneyline && !result.point_spread && !result.total_points) {
        // Try to use match_winner as moneyline
        if (odds.match_winner) {
            result.moneyline = {
                home: odds.match_winner.home_odds ? parseFloat(odds.match_winner.home_odds) : null,
                away: odds.match_winner.away_odds ? parseFloat(odds.match_winner.away_odds) : null
            };
        }
    }
    
    return result;
};


export default BasketballAPI;